import { DatePipe, formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { finalize, map, Observable, Subject } from 'rxjs';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { ProgramariService } from './programari.service';
import { AppFilesService } from 'src/app/services/app-files.service';
import { AddProgramareService } from '../cereri/add-programare/add-programare.service';
import Swal from 'sweetalert2';


@Component({
    selector: 'app-programari',
    templateUrl: './programari.component.html',
    styleUrls: ['./programari.component.scss']
})
export class ProgramariComponent {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;

    helper = helper;
    isLoading = false;
    isExporting = false;
    moduleId: number = -1;
    locatieId: number = -1;
    serviciiId: number = -1;
    tipProblemaId: number = -1;
    tipProgramareId: number = -1;
    dataProgramare: Date = new Date();
    formattedDate: string = formatDate(this.dataProgramare, 'yyyy-MM-dd', 'en-US');

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    modules$!: Observable<Generic[]>;
    locatii$!: Observable<Generic[]>;
    servicii$!: Observable<Generic[]>;
    tipuriProblema$!: Observable<Generic[]>;
    tipuriProgramare$!: Observable<Generic[]>;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink =
        environment.interop.basePath +
        environment.interop.platform.nomenclatoare.ghiseulUnic.programari.listDT;

    constructor(
        private http: HttpClient,
        public permissionsService: PermissionsService,
        private programariService: ProgramariService,
        private appFilesService: AppFilesService,
        private datePipe: DatePipe,
        private addProgramareService: AddProgramareService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.loadModules();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                dataTablesParameters.id_modul = this.moduleId;
                dataTablesParameters.id_locatie = this.locatieId;
                dataTablesParameters.id_serviciu = this.serviciiId;
                dataTablesParameters.id_tip_problema = this.tipProblemaId;
                dataTablesParameters.id_tip_programare = this.tipProgramareId;
                dataTablesParameters.date = this.formattedDate;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id_cerere',
                    width: '50px',
                },
                {
                    title: 'Cetățean',
                    data: 'full_name',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        let html = `<strong>${row.full_name || 'N/A'}</strong>`;
                        if (row.telefon) {
                            html += `<br/><small><i class="fa-solid fa-phone me-1"></i>${row.telefon}</small>`;
                        }
                        if (row.cnp) {
                            html += `<br/><small><i class="fa-solid fa-id-card me-1"></i>${row.cnp}</small>`;
                        }
                        return html;
                    },
                },
                {
                    title: 'Programare',
                    data: 'programare',
                    render: (data: any, type: any, row: any) => {
                        let html = `<strong>${row.programare || 'N/A'}</strong>`;
                        if (row.locatie_name) {
                            html += `<br/><small><i class="fa-solid fa-location-dot me-1"></i>${row.locatie_name}</small>`;
                        }
                        return html;
                    },
                },
                {
                    title: 'Tip Problemă',
                    data: 'tip_problema',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<span>${row.tip_problema || 'N/A'}</span>`;
                    },
                },
                {
                    title: 'Tip Programare',
                    data: 'tip_programare',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<span>${row.tip_programare || 'N/A'}</span>`;
                    },
                },
                {
                    title: 'Instituție/Serviciu',
                    data: 'serviciu_name',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        let html = '';
                        if (row.directie_name) {
                            html += `<strong>${row.directie_name}</strong>`;
                        }
                        if (row.compartiment_name) {
                            html += `<br/><small>${row.compartiment_name}</small>`;
                        }
                        if (row.serviciu_name) {
                            html += `<br/><small class="text-muted"><i class="fa-solid fa-caret-right me-1"></i>${row.serviciu_name}</small>`;
                        }
                        return html || 'N/A';
                    },
                },
                {
                    title: 'Dată Cerere',
                    data: 'created_at',
                    orderable: false,
                    width: '100px',
                    render: (data: any, type: any, row: any) => {
                        if (row.created_at) {
                            return `<span>${formatDate(row.created_at, 'dd.MM.yyyy HH:mm', 'en-US')}</span>`;
                        }
                        return `<span>N/A</span>`;
                    },
                },
                {
                    title: 'Dată Validare',
                    data: 'validated_date',
                    orderable: false,
                    width: '100px',
                    render: (data: any, type: any, row: any) => {
                        return `<span>${row.validated_date || 'N/A'}</span>`;
                    },
                },
            ],
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
    }

    loadModules() {
        this.isLoading = true;
        this.modules$ = this.addProgramareService.getModuleList().pipe(
            map((res: any) => {
                const data = (res?.data || []).map((item: any) => ({
                    id: item.id,
                    name: item.name,
                }));
                // Auto-select if single option
                if (data.length === 1) {
                    setTimeout(() => {
                        this.moduleId = data[0].id;
                        this.onModuleChange(data[0].id);
                    });
                }
                return [this.defaultParameter, ...data];
            }),
            finalize(() => (this.isLoading = false))
        );
    }

    onModuleChange(moduleId: number): void {
        // Reset dependent fields
        this.locatieId = -1;
        this.serviciiId = -1;
        this.tipProblemaId = -1;
        this.tipProgramareId = -1;

        // Reset dependent observables
        this.servicii$ = new Observable<Generic[]>((observer) => {
            observer.next([this.defaultParameter]);
            observer.complete();
        });
        this.tipuriProblema$ = new Observable<Generic[]>((observer) => {
            observer.next([this.defaultParameter]);
            observer.complete();
        });
        this.tipuriProgramare$ = new Observable<Generic[]>((observer) => {
            observer.next([this.defaultParameter]);
            observer.complete();
        });

        if (moduleId > 0) {
            this.findLocatiiByModul(moduleId);
        } else {
            this.locatii$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter]);
                observer.complete();
            });
        }

        this.rerenderDT(true);
    }

    findLocatiiByModul(moduleId: number) {
        this.isLoading = true;
        this.locatii$ = this.addProgramareService.getLocatiiByModul(moduleId).pipe(
            map((res: any) => {
                const data = (res?.data || []).map((item: any) => ({
                    id: item.id,
                    name: item.name,
                }));
                // Auto-select if single option
                if (data.length === 1) {
                    setTimeout(() => {
                        this.locatieId = data[0].id;
                        this.onLocatieChange(data[0].id);
                    });
                }
                return [this.defaultParameter, ...data];
            }),
            finalize(() => (this.isLoading = false))
        );
    }

    onLocatieChange(locatieId: number): void {
        // Reset dependent fields
        this.serviciiId = -1;
        this.tipProblemaId = -1;
        this.tipProgramareId = -1;

        // Reset dependent observables
        this.tipuriProblema$ = new Observable<Generic[]>((observer) => {
            observer.next([this.defaultParameter]);
            observer.complete();
        });
        this.tipuriProgramare$ = new Observable<Generic[]>((observer) => {
            observer.next([this.defaultParameter]);
            observer.complete();
        });

        if (this.moduleId > 0 && locatieId > 0) {
            this.loadServicii(this.moduleId, locatieId);
        } else {
            this.servicii$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter]);
                observer.complete();
            });
        }

        this.rerenderDT(true);
    }

    loadServicii(moduleId: number, locatieId: number) {
        this.isLoading = true;
        this.servicii$ = this.addProgramareService.getServiciiByModulLocatie(moduleId, locatieId).pipe(
            map((res: any) => {
                const data = (res?.data || []).map((item: any) => ({
                    id: item.id,
                    name: item.web_name || item.name,
                }));
                // Auto-select if single option
                if (data.length === 1) {
                    setTimeout(() => {
                        this.serviciiId = data[0].id;
                        this.onServiciuChange(data[0].id);
                    });
                }
                return [this.defaultParameter, ...data];
            }),
            finalize(() => (this.isLoading = false))
        );
    }

    onServiciuChange(serviciuId: number): void {
        // Reset dependent fields
        this.tipProblemaId = -1;
        this.tipProgramareId = -1;

        if (serviciuId > 0) {
            this.loadTipuriProblema(serviciuId);
            this.loadTipuriProgramare(this.moduleId, this.locatieId, serviciuId);
        } else {
            this.tipuriProblema$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter]);
                observer.complete();
            });
            this.tipuriProgramare$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter]);
                observer.complete();
            });
        }

        this.rerenderDT(true);
    }

    loadTipuriProblema(serviciuId: number) {
        this.tipuriProblema$ = this.addProgramareService.getTipuriProblemaByServiciu(serviciuId).pipe(
            map((res: any) => {
                const data = (res?.data || []).map((item: any) => ({
                    id: item.id,
                    name: item.name,
                }));
                // Auto-select if single option
                if (data.length === 1) {
                    setTimeout(() => {
                        this.tipProblemaId = data[0].id;
                        this.rerenderDT(true);
                    });
                }
                return [this.defaultParameter, ...data];
            })
        );
    }

    loadTipuriProgramare(moduleId: number, locatieId: number, serviciuId: number) {
        this.tipuriProgramare$ = this.addProgramareService.getTipuriProgramByConfig(moduleId, locatieId, serviciuId).pipe(
            map((res: any) => {
                const data = (res?.data || []).map((item: any) => ({
                    id: item.id,
                    name: item.name,
                }));
                // Auto-select if single option
                if (data.length === 1) {
                    setTimeout(() => {
                        this.tipProgramareId = data[0].id;
                        this.rerenderDT(true);
                    });
                }
                return [this.defaultParameter, ...data];
            })
        );
    }

    onDateChange(event: any) {
        this.formattedDate = formatDate(event.value, 'yyyy-MM-dd', 'en-US');
        this.rerenderDT(true);
    }

    printProgramari() {
        const fullPath = this.programariService.printListProgramariURL;
        const payload = {
            id_locatie: this.locatieId,
            id_modul: this.moduleId,
            id_serviciu: this.serviciiId,
            id_tip_problema: this.tipProblemaId,
            id_tip_programare: this.tipProgramareId,
            date: this.formattedDate,
        };

        this.isLoading = true;
        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download doc error: ', err);
                this.isLoading = false;
            })
    }

    exportExcel() {
        const payload = {
            id_locatie: this.locatieId,
            id_modul: this.moduleId,
            id_serviciu: this.serviciiId,
            id_tip_problema: this.tipProblemaId,
            id_tip_programare: this.tipProgramareId,
            date: this.formattedDate,
        };

        this.isExporting = true;
        this.programariService.exportExcel(payload).subscribe({
            next: (blob: Blob) => {
                this.isExporting = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `programari_${this.formattedDate || new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.isExporting = false;
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: 'Eroare la export. Va rugam reincercati.',
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                });
            },
        });
    }
}
